<div class="w-full">
  <!-- Single Header -->
  <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
    <div>
      <h3 class="text-xl font-bold text-[#1E2A78]">{{ 'FIN_ANALYSIS.TITLE' | translate }}</h3>

      <p class="mt-1 text-sm text-gray-500">
        {{ 'FIN_ANALYSIS.SUBTITLE' | translate }}
      </p>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <div class="rounded-lg bg-indigo-50 px-4 py-2 text-sm font-medium text-[#1E2A78]">
        {{ 'FIN_ANALYSIS.SELECTED' | translate }}:
        {{ selectedCount }}
      </div>

      <button
        type="button"
        [disabled]="loading || saving || !crdtPrpslSeq"
        (click)="refreshFinancialAnalysis()"
        class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ 'COMMON.REFRESH' | translate }}
      </button>

      <button
        *ngIf="editable"
        type="button"
        [disabled]="loading || saving || selectedCount === 0"
        (click)="addSelectedRows()"
        class="rounded-lg bg-[#1E2A78] px-5 py-2 text-sm font-medium text-white transition hover:bg-[#16205e] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ 'FIN_ANALYSIS.ADD_SELECTED_ROWS' | translate }}
      </button>

      <button
        *ngIf="editable && selectedCount > 0"
        type="button"
        [disabled]="saving"
        (click)="clearSelection()"
        class="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ 'FIN_ANALYSIS.CLEAR_SELECTION' | translate }}
      </button>
    </div>
  </div>

  <!-- Saving Status -->
  <div
    *ngIf="saving"
    class="mb-5 rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-[#1E2A78]"
  >
    {{ 'FIN_ANALYSIS.SAVING' | translate }}
  </div>

  <!-- Institution Name: Only Once -->
  <div
    *ngIf="!loading && rows.length > 0"
    class="mb-6 rounded-xl border border-indigo-100 bg-indigo-50 px-5 py-4"
  >
    <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">{{ 'FINANCIAL_DATA.INSTITUTION_NAME' | translate }}</p>

    <p class="mt-1 text-lg font-semibold text-[#1E2A78]">
      {{ institutionDisplayName }}
    </p>
  </div>

  <!-- Loading -->
  <div
    *ngIf="loading"
    class="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500"
  >
    {{ 'FIN_ANALYSIS.LOADING' | translate }}
  </div>

  <!-- Table -->
  <div
    *ngIf="!loading && rows.length > 0"
    class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
  >
    <div class="overflow-x-auto">
      <table class="w-full min-w-[920px] border-collapse">
        <thead>
          <tr class="bg-[#1E2A78] text-white">
            <!-- Select All -->
            <th class="w-14 border-e border-indigo-400 px-3 py-3 text-center">
              <input
                type="checkbox"
                [(ngModel)]="selectAllChecked"
                name="selectAllFinancialAnalysis"
                [disabled]="!editable || saving"
                (change)="onSelectAllChange()"
                class="h-4 w-4 rounded border-gray-300"
                [attr.aria-label]="'FIN_ANALYSIS.SELECT_ALL_ROWS' | translate"
              />
            </th>

            <!-- Serial Number -->
            <th class="w-16 border-e border-indigo-400 px-3 py-3 text-center text-sm font-semibold">
              {{ 'FIN_ANALYSIS.SR_HASH' | translate }}
            </th>

            <!-- Head (column heading intentionally left blank) -->
            <th
              class="min-w-[220px] border-e border-indigo-400 px-4 py-3 text-start text-sm font-semibold"
            ></th>

            <!-- Description -->
            <th
              class="min-w-[340px] border-e border-indigo-400 px-4 py-3 text-start text-sm font-semibold"
            >
              {{ 'COMMON.DESCRIPTION' | translate }}
            </th>

            <!-- Dynamic Period Columns -->
            <th
              *ngFor="let period of periodColumns; trackBy: trackByPeriod"
              class="min-w-[140px] border-e border-indigo-400 px-4 py-3 text-end text-sm font-semibold"
            >
              {{ period }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            *ngFor="let row of rows; let rowIndex = index; trackBy: trackByRow"
            class="border-b border-gray-200 transition hover:bg-indigo-50"
            [class.bg-indigo-50]="row.selected"
          >
            <!-- Checkbox -->
            <td class="border-e border-gray-200 px-3 py-4 text-center align-top">
              <input
                type="checkbox"
                [(ngModel)]="row.selected"
                [name]="
                  'financialAnalysisRow_' + rowIndex + '_' + row.fncTypSeq + '_' + row.fncValSeq
                "
                [disabled]="!editable || saving"
                (change)="onRowSelectionChange()"
                class="h-4 w-4 rounded border-gray-300 text-[#1E2A78]"
                [attr.aria-label]="(row.head || row.dscr) ? ('FIN_ANALYSIS.SELECT_ITEM' | translate: { item: row.head || row.dscr }) : ('FIN_ANALYSIS.SELECT_ROW_N' | translate: { n: rowIndex + 1 })"
              />
            </td>

            <!-- Serial Number -->
            <td
              class="border-e border-gray-200 px-3 py-4 text-center align-top font-medium text-gray-700"
            >
              {{ rowIndex + 1 }}
            </td>

            <!-- Grouped Head -->
            <td
              *ngIf="isFirstHeadRow(rowIndex)"
              [attr.rowspan]="getHeadRowSpan(rowIndex)"
              class="min-w-[220px] border-e border-b border-gray-200 bg-indigo-50 px-5 py-4 align-top"
            >
              <div class="font-semibold text-[#1E2A78]">
                {{ row.head || '-' }}
              </div>
            </td>

            <!-- Description -->
            <td class="border-e border-gray-200 px-4 py-4 align-top">
              <div class="whitespace-pre-line leading-6 text-gray-700">
                {{ row.dscr || '-' }}
              </div>
            </td>

            <!-- Period Values -->
            <td
              *ngFor="let period of periodColumns; trackBy: trackByPeriod"
              class="border-e border-gray-200 px-4 py-4 text-end align-top"
            >
              <ng-container *ngIf="getPeriodValue(row, period) !== null; else noPeriodValue">
                <span class="font-medium text-gray-800">
                  {{ getPeriodValue(row, period) | number: '1.0-2' }}
                </span>
              </ng-container>

              <ng-template #noPeriodValue>
                <span class="text-gray-400"> - </span>
              </ng-template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Footer -->
    <div
      class="flex flex-col gap-2 border-t border-gray-200 bg-gray-50 px-5 py-4 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between"
    >
      <span>
        {{ 'FIN_ANALYSIS.TOTAL_ROWS' | translate }}:
        {{ rows.length }}
      </span>

      <span>
        {{ 'FIN_ANALYSIS.SELECTED_ROWS' | translate }}:
        {{ selectedCount }}
      </span>
    </div>
  </div>

  <!-- Empty State -->
  <div
    *ngIf="!loading && rows.length === 0 && !errorMessage"
    class="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center"
  >
    <h4 class="font-semibold text-gray-700">{{ 'FIN_ANALYSIS.NO_DATA' | translate }}</h4>

    <p class="mt-2 text-sm text-gray-500">
      {{ 'FIN_ANALYSIS.NO_ROWS_RETURNED' | translate }}
    </p>
  </div>

  <!-- Selected Rows Preview -->
  <div
    *ngIf="selectedRows.length > 0"
    class="mt-6 rounded-xl border border-indigo-100 bg-indigo-50 p-5"
  >
    <div class="flex items-center justify-between">
      <h4 class="font-semibold text-[#1E2A78]">{{ 'FIN_ANALYSIS.SELECTED_ROWS_TITLE' | translate }}</h4>

      <span class="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#1E2A78]">
        {{ selectedRows.length }}
      </span>
    </div>

    <div class="mt-4 flex flex-wrap gap-2">
      <span
        *ngFor="let row of selectedRows; let index = index"
        class="rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-xs font-medium text-[#1E2A78]"
      >
        {{ index + 1 }}.
        {{ row.head || row.dscr || ('FIN_ANALYSIS.ROW_FALLBACK' | translate) }}
      </span>
    </div>
  </div>
</div>
